import * as THREE from 'three';

/**
 * Soft camera-facing sprites in one draw call, each with its own place,
 * size and opacity (ADR-032): the volcano's plume, a beaker's steam. Turned
 * to the viewer in the vertex shader, per eye, so nothing is computed per
 * frame on the CPU but where each one is.
 */

const vertexShader = /* glsl */ `
#include <common>
#include <fog_pars_vertex>
attribute vec3 offset;
attribute float size;
attribute float alpha;
attribute float spin;
attribute vec3 tint;
varying vec2 vUv;
varying float vAlpha;
varying vec3 vTint;
void main() {
  // A puff not yet out, or gone: off the screen, so it costs no fill.
  if (alpha <= 0.0) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    return;
  }
  vUv = uv;
  vAlpha = alpha;
  vTint = tint;
  float c = cos(spin);
  float s = sin(spin);
  vec2 corner = mat2(c, s, -s, c) * position.xy * size;
  vec4 mvPosition = modelViewMatrix * vec4(offset, 1.0);
  mvPosition.xy += corner;
  gl_Position = projectionMatrix * mvPosition;
  #include <fog_vertex>
}
`;

const fragmentShader = /* glsl */ `
#include <common>
#include <fog_pars_fragment>
uniform sampler2D uMap;
uniform vec3 uColour;
uniform float uOpacity;
varying vec2 vUv;
varying float vAlpha;
varying vec3 vTint;
void main() {
  vec4 texel = texture2D(uMap, vUv);
  gl_FragColor = vec4(uColour * vTint * texel.rgb, texel.a * vAlpha * uOpacity);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
  #include <fog_fragment>
}
`;

export class SpriteCloud {
  readonly mesh: THREE.Mesh<THREE.InstancedBufferGeometry, THREE.ShaderMaterial>;
  readonly #offset: THREE.InstancedBufferAttribute;
  readonly #size: THREE.InstancedBufferAttribute;
  readonly #alpha: THREE.InstancedBufferAttribute;
  readonly #spin: THREE.InstancedBufferAttribute;
  /** Each sprite's colour, times the cloud's: an ash column lit orange from below. */
  readonly #tint: THREE.InstancedBufferAttribute;

  constructor(
    count: number,
    options: {
      map: THREE.Texture;
      colour: THREE.ColorRepresentation;
      opacity?: number;
      additive?: boolean;
      name: string;
    },
  ) {
    const quad = new THREE.PlaneGeometry(1, 1);
    const geometry = new THREE.InstancedBufferGeometry();
    geometry.index = quad.index;
    geometry.setAttribute('position', quad.getAttribute('position'));
    geometry.setAttribute('uv', quad.getAttribute('uv'));
    this.#offset = new THREE.InstancedBufferAttribute(new Float32Array(count * 3), 3);
    this.#size = new THREE.InstancedBufferAttribute(new Float32Array(count), 1);
    this.#alpha = new THREE.InstancedBufferAttribute(new Float32Array(count), 1);
    this.#spin = new THREE.InstancedBufferAttribute(new Float32Array(count), 1);
    this.#tint = new THREE.InstancedBufferAttribute(new Float32Array(count * 3).fill(1), 3);
    for (const attribute of [this.#offset, this.#size, this.#alpha, this.#spin, this.#tint]) {
      attribute.setUsage(THREE.DynamicDrawUsage);
    }
    geometry.setAttribute('offset', this.#offset);
    geometry.setAttribute('size', this.#size);
    geometry.setAttribute('alpha', this.#alpha);
    geometry.setAttribute('spin', this.#spin);
    geometry.setAttribute('tint', this.#tint);
    geometry.instanceCount = count;
    const material = new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      fog: true,
      transparent: true,
      depthWrite: false,
      blending: options.additive ? THREE.AdditiveBlending : THREE.NormalBlending,
      uniforms: THREE.UniformsUtils.merge([
        THREE.UniformsLib.fog,
        { uMap: { value: null }, uColour: { value: new THREE.Color() }, uOpacity: { value: 1 } },
      ]),
    });
    material.uniforms['uMap']!.value = options.map;
    (material.uniforms['uColour']!.value as THREE.Color).set(options.colour);
    material.uniforms['uOpacity']!.value = options.opacity ?? 1;
    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.name = options.name;
    // Its sprites move about; its bounding sphere would not.
    this.mesh.frustumCulled = false;
    this.mesh.raycast = (): void => {};
  }

  get count(): number {
    return this.#size.count;
  }

  set(
    i: number,
    position: THREE.Vector3Like,
    size: number,
    alpha: number,
    spin = 0,
    tint?: THREE.Color,
  ): void {
    this.#offset.setXYZ(i, position.x, position.y, position.z);
    if (tint) this.#tint.setXYZ(i, tint.r, tint.g, tint.b);
    this.#size.setX(i, size);
    this.#alpha.setX(i, alpha);
    this.#spin.setX(i, spin);
  }

  commit(): void {
    for (const attribute of [this.#offset, this.#size, this.#alpha, this.#spin, this.#tint]) {
      attribute.needsUpdate = true;
    }
  }
}
